import copy from '../copy';
import * as validators from '../helpers/validators';

const { errorMessages } = copy;

const isEmptyString = validators.checkString({ maxLength: 0 });
const isNonEmptyString = validators.checkString({ minLength: 1 });

export const userTypes = {
    CONSUMER: 'consumer',
    DEALER: 'dealer',
    TRADE: 'trade',
} as const;

export const questionNames = {
    INTENT: 'INTENT',
    CATEGORIES: 'CATEGORIES',
    L2: 'L2',
} as const;

export const stepLabels = {
    DISPLAYED: 'displayed',
    COMPLETED: 'completed',
    SKIPPED: 'skipped',
} as const;

export const answerNames = {
    GIFT: 'GIFT',
} as const;

export const tradeApplicationInfoKey = 'tradeApplicationInfo' as const;
export const steps = {
    USER_TYPE_SELECTION: 'user_type_selection',
    CONSUMER_BENEFITS: 'consumer_benefits',
    CONSUMER_COMPLETE: 'consumer_complete',
    CONSUMER_INFO: 'consumer_info',
    DEALER_INFO: 'dealer_info',
    TRADE_APPLICATION_STEP_1: 'trade_application_step_1',
    TRADE_APPLICATION_STEP_2: 'trade_application_step_2',
    TRADE_REFERRAL: 'trade_referral',
    TRADE_COMPLETE: 'trade_complete',
    PROMO_CODE: 'promo_code',
    USER_SURVEY: 'user_survey',
    USER_SURVEY_CONFIRMATION: 'user_survey_confirmation',
    USER_SURVEY_GIFT_CONFIRMATION: 'user_survey_gift_confirmation',
} as const;

export const gaLabels = {
    PAGEVIEW_LIMIT_REACHED: 'pageview limit reached',
} as const;

export const events = {
    USER_CHANGED: 'user_changed',
} as const;
export type Events = keyof typeof events;
export type EventValues = typeof events[Events];

const registrationLeadSourceLabel = 'Web: User Registration' as const;
export const flowActions = {
    AUTO_PROMPT_REGISTER: {
        leadSourceLabel: registrationLeadSourceLabel,
        startingStep: steps.USER_TYPE_SELECTION,
    },
    LOGIN: {
        leadSourceLabel: 'Web: User Login',
        startingStep: steps.USER_TYPE_SELECTION,
    },
    REGISTER: {
        leadSourceLabel: registrationLeadSourceLabel,
        startingStep: steps.USER_TYPE_SELECTION,
    },
    SOCIAL_REGISTER_FACEBOOK: {
        leadSourceLabel: 'Web: Social Registration',
        tradeLeadSourceLabel: 'Web: Social Registration',
        startingStep: steps.USER_TYPE_SELECTION,
    },
    SOCIAL_REGISTER_GOOGLE: {
        leadSourceLabel: 'Web: Social Registration',
        tradeLeadSourceLabel: 'Web: Social Registration',
        startingStep: steps.USER_TYPE_SELECTION,
    },
    TRADE_APPLICATION: {
        leadSourceLabel: 'Web: Trade Registration',
        startingStep: steps.TRADE_APPLICATION_STEP_1,
    },
    SUSPECTED_TRADE_APPLICATION: {
        leadSourceLabel: 'Web: Trade Registration',
        startingStep: steps.TRADE_APPLICATION_STEP_1,
    },
    TRADE_REAPPLY: {
        leadSourceLabel: 'Firmless: Reapplied',
        startingStep: steps.TRADE_APPLICATION_STEP_1,
    },
    ALT_REGISTRATION_CONSUMER_FLOW: {
        leadSourceLabel: registrationLeadSourceLabel,
        startingStep: steps.USER_SURVEY,
    },
} as const;
export type FlowActions = keyof typeof flowActions;

export const footers = {
    ASK_LATER: 'ask_later_footer',
    REQUEST_PASSWORD_EMAIL: 'request_password_email_footer',
    LOGIN: 'login_footer',
    REGISTER: 'register_footer',
    SKIP_QUESTION: 'skip_question',
} as const;

export const tradeFormFields = {
    FIRST_NAME: 'FIRST_NAME',
    LAST_NAME: 'LAST_NAME',
    FIRM_NAME: 'FIRM_NAME',
    FIRM_WEBSITE: 'FIRM_WEBSITE',
    PHONE_NUMBER: 'PHONE_NUMBER',
    EMAIL: 'EMAIL',
    // CAM-461 SALESFORCE-2890
    // HAS_OFFICIAL_DOCS is a misnomer - it should really be "FOLLOW_UP_FOR_DOCUMENTATION"
    // but instead of doing a code change, which require coordinating with FE and SF,
    // this is going to stay as-is but noting that in SF, this indicates that the
    // Firm Management will follow-up with the Lead to get more documentation
    HAS_OFFICIAL_DOCS: 'HAS_OFFICIAL_DOCS',
    TRADE_DOCUMENT: 'TRADE_DOCUMENT',
    SOCIAL_NETWORK: 'SOCIAL_NETWORK',
    SOCIAL_USERNAME: 'SOCIAL_USERNAME',
    FIRM_SIZE: 'FIRM_SIZE',
    ROLE_AT_FIRM: 'ROLE_AT_FIRM',
    FIRM_TYPE: 'FIRM_TYPE',
    FIRM_ADDRESS: 'FIRM_ADDRESS',
    FIRM_POSTAL_CODE: 'FIRM_POSTAL_CODE',
    FIRM_COUNTRY: 'FIRM_COUNTRY',
    USER_EXISTS: 'USER_EXISTS',
    REASON_FOR_JOINING: 'REASON_FOR_JOINING',
    TRADE_USER_STATUS: 'TRADE_USER_STATUS',
} as const;

export const tradeFormFieldsConfig = {
    [tradeFormFields.FIRST_NAME]: {
        defaultValue: '',
        errorMessage: errorMessages?.firstName,
        validator: validators.checkString({ minLength: 1, maxLength: 40 }),
    },
    [tradeFormFields.LAST_NAME]: {
        defaultValue: '',
        errorMessage: errorMessages?.lastName,
        validator: validators.checkString({ minLength: 1, maxLength: 80 }),
    },
    [tradeFormFields.FIRM_NAME]: {
        defaultValue: '',
        errorMessage: errorMessages?.firmName,
        validator: isNonEmptyString,
    },
    [tradeFormFields.FIRM_WEBSITE]: {
        defaultValue: '',
        errorMessage: null,
        validator: validators.oneOf(isEmptyString, validators.isValidUrl),
    },
    [tradeFormFields.PHONE_NUMBER]: {
        defaultValue: '',
        errorMessage: errorMessages?.phoneNumber,
        validator: validators.checkString({ minLength: 9 }),
    },
    [tradeFormFields.EMAIL]: {
        defaultValue: '',
        errorMessage: errorMessages?.emailInvalid,
        validator: validators.all(
            validators.isValidEmail,
            validators.checkString({ maxLength: 80 })
        ),
    },
    [tradeFormFields.HAS_OFFICIAL_DOCS]: {
        defaultValue: false,
        errorMessage: null,
        validator: null,
    },
    [tradeFormFields.TRADE_DOCUMENT]: {
        defaultValue: null,
        errorMessage: null,
        validator: null,
    },
    [tradeFormFields.SOCIAL_NETWORK]: {
        defaultValue: '',
        errorMessage: null,
        validator: null,
    },
    [tradeFormFields.SOCIAL_USERNAME]: {
        defaultValue: '',
        errorMessage: null,
        validator: null,
    },
    [tradeFormFields.FIRM_SIZE]: {
        defaultValue: '',
        errorMessage: errorMessages?.firmSize,
        validator: isNonEmptyString,
    },
    [tradeFormFields.ROLE_AT_FIRM]: {
        defaultValue: '',
        errorMessage: errorMessages?.roleAtFirm,
        validator: isNonEmptyString,
    },
    [tradeFormFields.FIRM_TYPE]: {
        defaultValue: '',
        errorMessage: errorMessages?.firmType,
        validator: isNonEmptyString,
    },
    [tradeFormFields.FIRM_ADDRESS]: {
        defaultValue: '',
        errorMessage: errorMessages?.firmAddress,
        validator: isNonEmptyString,
    },
    [tradeFormFields.FIRM_POSTAL_CODE]: {
        defaultValue: '',
        errorMessage: errorMessages?.firmZipCode,
        validator: validators.all(isNonEmptyString, validators.isValidZipcode),
    },
    [tradeFormFields.FIRM_COUNTRY]: {
        defaultValue: '',
        errorMessage: errorMessages?.firmCountry,
        validator: isNonEmptyString,
    },
    [tradeFormFields.USER_EXISTS]: {
        defaultValue: false,
        errorMessage: null,
        validator: null,
    },
    [tradeFormFields.REASON_FOR_JOINING]: {
        defaultValue: '',
        errorMessage: errorMessages?.reasonForJoining,
        validator: isNonEmptyString,
    },
    [tradeFormFields.TRADE_USER_STATUS]: {
        defaultValue: '',
        errorMessage: null,
        validator: null,
    },
} as const;
export type TradeFormFields = keyof typeof tradeFormFieldsConfig;
